import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Star, 
  Leaf, 
  TrendingUp, 
  Shield, 
  Users, 
  Globe,
  FileText,
  DollarSign,
  Target,
  Award,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Mock project data - replace with API call
  const mockProject = {
    id: parseInt(id),
    title: "Amazon Rainforest Conservation",
    description: "Protecting 10,000 hectares of pristine Amazon rainforest through community-based conservation initiatives. This project focuses on preventing deforestation while supporting local indigenous communities through sustainable economic opportunities.",
    location: {
      country: "Brazil",
      region: "Amazonas State",
      coordinates: "-3.4653, -62.2159"
    },
    projectType: "Forest Conservation",
    price: 25,
    totalCredits: 50000,
    availableCredits: 35000,
    verificationStatus: "Gold Standard",
    rating: 4.8,
    reviewCount: 42,
    images: [
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1574263867128-a3a4feb8bb6a?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop"
    ],
    developer: {
      name: "EcoForest Initiative",
      type: "Non-Profit Organization",
      experience: "15+ years",
      contact: "contact@ecoforest.org",
      website: "www.ecoforest.org"
    },
    timeline: {
      startDate: "2023-01-15",
      completionDate: "2025-12-31",
      vintageYear: 2024
    },
    impactMetrics: {
      co2Avoided: "125,000 tons",
      biodiversityProtected: "500+ species",
      communitiesSupported: 12,
      jobsCreated: 85,
      landsProtected: "10,000 hectares"
    },
    verification: {
      standard: "Gold Standard",
      body: "SCS Global Services",
      registryId: "GS-12345-BR-2024",
      methodology: "VM0007 REDD+ Methodology Framework"
    },
    financials: {
      totalValue: 1250000,
      minimumPurchase: 10,
      currency: "USD"
    },
    sdgAlignment: [
      "Climate Action",
      "Life on Land", 
      "No Poverty",
      "Decent Work and Economic Growth",
      "Partnerships for the Goals"
    ],
    documents: [
      { name: "Project Design Document", type: "PDF", size: "2.3 MB" },
      { name: "Verification Report", type: "PDF", size: "1.8 MB" },
      { name: "Environmental Impact Assessment", type: "PDF", size: "3.1 MB" },
      { name: "Community Engagement Plan", type: "PDF", size: "1.2 MB" }
    ],
    monitoring: {
      reportingFrequency: "Quarterly",
      lastUpdate: "2024-01-15",
      transparency: "Public"
    }
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setProject(mockProject);
      setLoading(false);
    }, 1000);
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
        <p className="text-gray-600 mb-6">The project you're looking for doesn't exist.</p>
        <Link to="/marketplace" className="text-green-600 hover:text-green-700">
          Return to Marketplace
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'impact', label: 'Impact', icon: Target },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'monitoring', label: 'Monitoring', icon: TrendingUp }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Project Description</h3>
              <p className="text-gray-700 leading-relaxed">{project.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Project Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{project.projectType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Date:</span>
                    <span className="font-medium">{new Date(project.timeline.startDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completion:</span>
                    <span className="font-medium">{new Date(project.timeline.completionDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vintage Year:</span>
                    <span className="font-medium">{project.timeline.vintageYear}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Developer Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Organization:</span>
                    <span className="font-medium">{project.developer.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{project.developer.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience:</span>
                    <span className="font-medium">{project.developer.experience}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Website:</span>
                    <a href={`https://${project.developer.website}`} target="_blank" rel="noopener noreferrer" 
                       className="font-medium text-green-600 hover:text-green-700 flex items-center">
                      {project.developer.website}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">SDG Alignment</h4>
              <div className="flex flex-wrap gap-2">
                {project.sdgAlignment.map((sdg, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {sdg}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      case 'impact':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-green-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <Leaf className="h-8 w-8 text-green-600 mr-3" />
                  <h4 className="font-semibold text-gray-900">CO2 Impact</h4>
                </div>
                <p className="text-2xl font-bold text-green-600">{project.impactMetrics.co2Avoided}</p>
                <p className="text-sm text-gray-600">CO2 equivalent avoided</p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <Globe className="h-8 w-8 text-blue-600 mr-3" />
                  <h4 className="font-semibold text-gray-900">Biodiversity</h4>
                </div>
                <p className="text-2xl font-bold text-blue-600">{project.impactMetrics.biodiversityProtected}</p>
                <p className="text-sm text-gray-600">Protected species</p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <Users className="h-8 w-8 text-purple-600 mr-3" />
                  <h4 className="font-semibold text-gray-900">Communities</h4>
                </div>
                <p className="text-2xl font-bold text-purple-600">{project.impactMetrics.communitiesSupported}</p>
                <p className="text-sm text-gray-600">Communities supported</p>
              </div>

              <div className="bg-yellow-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <TrendingUp className="h-8 w-8 text-yellow-600 mr-3" />
                  <h4 className="font-semibold text-gray-900">Employment</h4>
                </div>
                <p className="text-2xl font-bold text-yellow-600">{project.impactMetrics.jobsCreated}</p>
                <p className="text-sm text-gray-600">Jobs created</p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <MapPin className="h-8 w-8 text-red-600 mr-3" />
                  <h4 className="font-semibold text-gray-900">Land Protection</h4>
                </div>
                <p className="text-2xl font-bold text-red-600">{project.impactMetrics.landsProtected}</p>
                <p className="text-sm text-gray-600">Land area protected</p>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Environmental Benefits</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Direct Benefits</h5>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Prevents deforestation of primary forest</li>
                    <li>• Maintains critical wildlife corridors</li>
                    <li>• Preserves watershed functions</li>
                    <li>• Protects indigenous territories</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Co-Benefits</h5>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Sustainable livelihood opportunities</li>
                    <li>• Environmental education programs</li>
                    <li>• Traditional knowledge preservation</li>
                    <li>• Ecotourism development</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'verification':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Verification Status</h3>
                  <p className="text-sm text-gray-600">This project has been verified under international standards</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Verification Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Standard:</span>
                      <span className="font-medium">{project.verification.standard}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Verification Body:</span>
                      <span className="font-medium">{project.verification.body}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Registry ID:</span>
                      <span className="font-medium">{project.verification.registryId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Methodology:</span>
                      <span className="font-medium">{project.verification.methodology}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Quality Assurance</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Third-party verification completed</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Additionality demonstrated</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Monitoring plan approved</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Registry listing confirmed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Certification Timeline</h4>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-green-600">1</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">Project Development</p>
                    <p className="text-sm text-gray-600">Initial planning and methodology selection</p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">Q1 2023</div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-green-600">2</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">Validation</p>
                    <p className="text-sm text-gray-600">Third-party validation of project design</p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">Q2 2023</div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-green-600">3</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">Registration</p>
                    <p className="text-sm text-gray-600">Registry approval and listing</p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">Q3 2023</div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-blue-600">4</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">Monitoring & Verification</p>
                    <p className="text-sm text-gray-600">Ongoing monitoring and periodic verification</p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">Ongoing</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Documentation</h3>
              <div className="grid grid-cols-1 gap-4">
                {project.documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center">
                      <FileText className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        <p className="text-sm text-gray-600">{doc.type} • {doc.size}</p>
                      </div>
                    </div>
                    <button className="text-green-600 hover:text-green-700 font-medium">
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-yellow-800">Document Access</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Some documents may require registration or purchase to access. All critical verification documents are publicly available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'monitoring':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Reporting Frequency</h4>
                <p className="text-2xl font-bold text-blue-600">{project.monitoring.reportingFrequency}</p>
                <p className="text-sm text-gray-600">Monitoring reports</p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Last Update</h4>
                <p className="text-2xl font-bold text-green-600">
                  {new Date(project.monitoring.lastUpdate).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">Latest data</p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Transparency</h4>
                <p className="text-2xl font-bold text-purple-600 capitalize">{project.monitoring.transparency}</p>
                <p className="text-sm text-gray-600">Data access level</p>
              </div>
            </div>

            <div className="bg-white border p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Monitoring Approach</h4>
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Satellite Monitoring</h5>
                  <p className="text-sm text-gray-700">
                    High-resolution satellite imagery is used to monitor deforestation and forest degradation in real-time.
                    Monthly analysis provides early warning of potential threats.
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Ground Verification</h5>
                  <p className="text-sm text-gray-700">
                    Local community members conduct regular field surveys to verify satellite data and monitor biodiversity indicators.
                    Training programs ensure data quality and local capacity building.
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Third-Party Audits</h5>
                  <p className="text-sm text-gray-700">
                    Independent verification bodies conduct annual audits to ensure compliance with methodology requirements
                    and validate emission reduction calculations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link to="/marketplace" className="mr-4">
            <ArrowLeft className="h-6 w-6 text-gray-600 hover:text-gray-800" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-3">
                {project.projectType}
              </span>
              <div className="flex items-center">
                <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                <span className="text-sm font-medium text-gray-700">{project.rating}</span>
                <span className="text-sm text-gray-500 ml-1">({project.reviewCount} reviews)</span>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="mr-4">{project.location.country}, {project.location.region}</span>
              <Calendar className="h-4 w-4 mr-1" />
              <span>Completion: {new Date(project.timeline.completionDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Image Gallery */}
          <div className="mb-8">
            <div className="aspect-w-16 aspect-h-9 mb-4">
              <img
                src={project.images[selectedImageIndex]}
                alt={project.title}
                className="w-full h-64 sm:h-80 object-cover rounded-lg"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {project.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`aspect-w-16 aspect-h-9 rounded-lg overflow-hidden ${
                    selectedImageIndex === index ? 'ring-2 ring-green-500' : ''
                  }`}
                >
                  <img
                    src={image}
                    alt={`View ${index + 1}`}
                    className="w-full h-16 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mb-8">
            {renderTabContent()}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-8 space-y-6">
            {/* Purchase Card */}
            <div className="bg-white border rounded-lg p-6 shadow-sm">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-green-600 mb-1">
                  ${project.price}
                </div>
                <div className="text-sm text-gray-600">per carbon credit</div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Available Credits:</span>
                  <span className="font-medium">{project.availableCredits.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Credits:</span>
                  <span className="font-medium">{project.totalCredits.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Min. Purchase:</span>
                  <span className="font-medium">{project.financials.minimumPurchase} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Verification:</span>
                  <span className="font-medium flex items-center">
                    <Shield className="h-4 w-4 text-green-600 mr-1" />
                    {project.verificationStatus}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{Math.round((1 - project.availableCredits / project.totalCredits) * 100)}% sold</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(1 - project.availableCredits / project.totalCredits) * 100}%` }}
                  ></div>
                </div>
              </div>

              <Link
                to={`/marketplace/purchase/${project.id}`}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                Purchase Credits
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>

              <div className="mt-4 text-xs text-gray-500 text-center">
                Secure payment • Instant certificate delivery
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">CO2 Impact</span>
                  <span className="text-sm font-medium">{project.impactMetrics.co2Avoided}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Communities</span>
                  <span className="text-sm font-medium">{project.impactMetrics.communitiesSupported}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Jobs Created</span>
                  <span className="text-sm font-medium">{project.impactMetrics.jobsCreated}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Land Protected</span>
                  <span className="text-sm font-medium">{project.impactMetrics.landsProtected}</span>
                </div>
              </div>
            </div>

            {/* Developer Info */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Project Developer</h3>
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-gray-900">{project.developer.name}</p>
                  <p className="text-sm text-gray-600">{project.developer.type}</p>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Award className="h-4 w-4 mr-2" />
                  {project.developer.experience}
                </div>
                <div className="flex items-center text-sm">
                  <ExternalLink className="h-4 w-4 mr-2 text-gray-400" />
                  <a href={`https://${project.developer.website}`} target="_blank" rel="noopener noreferrer" 
                     className="text-green-600 hover:text-green-700">
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails; 